import { useRef, useEffect } from "react";
import { X } from "lucide-react";

interface TermsAndConditionsProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TermsAndConditions({
  isOpen,
  onClose,
}: TermsAndConditionsProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div
        ref={modalRef}
        className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[90vh]">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Terms and Conditions
            </h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="prose max-w-none text-gray-700 space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                1. Account Registration
              </h2>
              <p className="mb-3">
                By creating an account with AirArrange, you agree to the
                following terms:
              </p>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  You must provide accurate, current, and complete information
                  during registration
                </li>
                <li>
                  You are responsible for maintaining the confidentiality of
                  your account credentials
                </li>
                <li>
                  You must be at least 18 years of age to create an account
                </li>
                <li>
                  One person may not maintain more than one account without
                  prior written consent
                </li>
                <li>
                  You agree to notify us immediately of any unauthorized use of
                  your account
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                2. Platform Role (No Agency)
              </h2>
              <p className="mb-3">
                AirArrange operates as a technology marketplace only.
              </p>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  We do not act as a real estate agent, broker, property
                  manager, or representative for any user.
                </li>
                <li>
                  We do not negotiate pricing, duration, or terms of any
                  booking.
                </li>
                <li>
                  All prices, availability, and conditions are set solely by the
                  workspace provider (Host).
                </li>
                <li>
                  Any licence, lease, or occupancy agreement is entered into
                  directly between the Host and the Guest.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                3. Personal Information and Privacy
              </h2>
              <p className="mb-3">
                We are committed to protecting your personal information in
                accordance with applicable privacy laws:
              </p>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  We collect personal information only as necessary to provide
                  our services
                </li>
                <li>
                  Your personal data is processed in accordance with GDPR, CCPA,
                  and other applicable privacy regulations
                </li>
                <li>
                  We do not sell, rent, or lease your personal information to
                  third parties
                </li>
                <li>
                  You have the right to access, modify, or delete your personal
                  information
                </li>
                <li>
                  We implement appropriate security measures to protect your
                  sensitive information
                </li>
                <li>
                  Payment information is encrypted and processed through secure
                  payment gateways
                </li>
                <li>
                  We may use cookies and similar technologies to enhance your
                  experience
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                4. Service Usage
              </h2>
              <p className="mb-3">
                Your use of our workspace booking platform is subject to these
                conditions:
              </p>
              <ul className="space-y-2 list-disc pl-6">
                <li>You may only use our services for lawful purposes</li>
                <li>You agree not to interfere with or disrupt our services</li>
                <li>
                  You are responsible for all activities that occur under your
                  account
                </li>
                <li>
                  We reserve the right to suspend or terminate accounts that
                  violate these terms
                </li>
                <li>
                  Content you submit must not infringe on intellectual property
                  rights
                </li>
                <li>
                  You grant us a license to use content you submit for service
                  provision purposes
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                5. Booking and Payment Terms
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  Bookings are completed through a fixed-price, non-negotiable
                  click-to-book process.
                </li>
                <li>
                  Payment is required at the time of booking unless otherwise
                  specified.
                </li>
                <li>
                  Cancellation policies are set by Hosts and displayed prior to
                  booking.
                </li>
                <li>
                  We charge a percentage-based platform service fee for use of
                  the Platform, which is disclosed during the booking process.
                  The total service fee is 6% of the total booking amount.
                </li>
                <li>
                  The platform service fee is not a commission, brokerage fee,
                  or letting fee.
                </li>
                <li>
                  Hosts are responsible for any applicable taxes arising from
                  bookings.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                5. Liability and Disclaimers
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  Our services are provided "as is" without warranties of any
                  kind
                </li>
                <li>
                  We are not liable for damages arising from your use of our
                  services
                </li>
                <li>
                  We do not guarantee the availability or quality of workspace
                  providers
                </li>
                <li>
                  You agree to indemnify us against claims arising from your use
                  of our services
                </li>
                <li>
                  Our total liability is limited to the amount paid for the
                  specific service
                </li>
              </ul>
              <p className="mt-3">
                We are not responsible for negotiating, arranging, or advising
                on any booking, lease, or occupancy arrangement between users.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                6. Data Security and Compliance
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  We employ industry-standard security measures to protect your
                  data
                </li>
                <li>
                  Data breaches will be reported in accordance with applicable
                  laws
                </li>
                <li>
                  We comply with relevant data protection regulations in your
                  jurisdiction
                </li>
                <li>
                  Cross-border data transfers are conducted with appropriate
                  safeguards
                </li>
                <li>
                  Regular security audits are performed to maintain data
                  integrity
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                7. Modifications and Termination
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  We reserve the right to modify these terms at any time with
                  notice
                </li>
                <li>
                  Continued use of our services constitutes acceptance of
                  modified terms
                </li>
                <li>
                  You may terminate your account at any time through your
                  account settings
                </li>
                <li>We may terminate accounts for violations of these terms</li>
                <li>
                  Upon termination, certain provisions of these terms will
                  continue to apply
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                8. Contact Information
              </h2>
              <p>
                If you have questions about these terms and conditions, please
                contact us at:
              </p>
              <ul className="space-y-2 list-disc pl-6 mt-3">
                <li>Email: contact@airarrange.com</li>
                <li>Phone: +1 (555) 123-4567</li>
                <li>
                  Address: 123 Business Street, Suite 100, City, State 12345
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                9. Governing Law
              </h2>
              <p>
                These Terms and Conditions are governed by the laws of New South
                Wales, Australia.
              </p>
            </section>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                By creating an account, you acknowledge that you have read,
                understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
